import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { createContactFormSchema, type ContactFormSchema } from '@/schemas/contactSchema'
import { contactService } from '@/services/api'
import { applyPhoneMask } from '@/utils/phoneMask'
import { Button } from '@/components/Button'
import { useMemo, useState } from 'react'
import { useLanguage } from '@/i18n/LanguageContext'

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error'

export function ContactForm() {
  const [submitStatus, setSubmitStatus] = useState<SubmitStatus>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const { t, language } = useLanguage()

  const contactFormSchema = useMemo(() => {
    return createContactFormSchema(t.contato.form.validation)
  }, [t])

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<ContactFormSchema>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      company: '',
      email: '',
      phone: '',
      subject: '',
      message: '',
    },
  })

  const phoneValue = watch('phone')
  const { ref: phoneRef, ...restPhone } = register('phone')

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyPhoneMask(e.target.value)
    setValue('phone', masked, { shouldValidate: true })
  }

  const onSubmit = async (data: ContactFormSchema) => {
    setSubmitStatus('loading')
    setSubmitMessage('')

    try {
      await contactService.send(data, language)
      setSubmitStatus('success')
      setSubmitMessage(t.contato.form.feedback.success)
      reset()
    } catch (err) {
      setSubmitStatus('error')
      setSubmitMessage(err instanceof Error ? err.message : t.contato.form.feedback.errorFallback)
    }
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 mb-1">
            {t.contato.form.labels.name} <span className="text-red-500">*</span>
          </label>
          <input
            id="contact-name"
            type="text"
            autoComplete="name"
            className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
            placeholder={t.contato.form.placeholders.name}
            {...register('name')}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'name-error' : undefined}
          />
          {errors.name && (
            <p id="name-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.name.message}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="contact-company" className="block text-sm font-medium text-slate-700 mb-1">
            {t.contato.form.labels.company} <span className="text-red-500">*</span>
          </label>
          <input
            id="contact-company"
            type="text"
            autoComplete="organization"
            className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
            placeholder={t.contato.form.placeholders.company}
            {...register('company')}
            aria-invalid={!!errors.company}
            aria-describedby={errors.company ? 'company-error' : undefined}
          />
          {errors.company && (
            <p id="company-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.company.message}
            </p>
          )}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 mb-1">
            {t.contato.form.labels.email} <span className="text-red-500">*</span>
          </label>
          <input
            id="contact-email"
            type="email"
            autoComplete="email"
            className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
            placeholder={t.contato.form.placeholders.email}
            {...register('email')}
            aria-invalid={!!errors.email}
            aria-describedby={errors.email ? 'email-error' : undefined}
          />
          {errors.email && (
            <p id="email-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="contact-phone" className="block text-sm font-medium text-slate-700 mb-1">
            {t.contato.form.labels.phone} <span className="text-red-500">*</span>
          </label>
          <input
            id="contact-phone"
            type="tel"
            autoComplete="tel"
            ref={phoneRef}
            {...restPhone}
            className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
            placeholder={t.contato.form.placeholders.phone}
            value={phoneValue ?? ''}
            onChange={handlePhoneChange}
            aria-invalid={!!errors.phone}
            aria-describedby={errors.phone ? 'phone-error' : undefined}
          />
          {errors.phone && (
            <p id="phone-error" className="mt-1 text-sm text-red-600" role="alert">
              {errors.phone.message}
            </p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="contact-subject" className="block text-sm font-medium text-slate-700 mb-1">
          {t.contato.form.labels.subject} <span className="text-red-500">*</span>
        </label>
        <input
          id="contact-subject"
          type="text"
          className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition"
          placeholder={t.contato.form.placeholders.subject}
          {...register('subject')}
          aria-invalid={!!errors.subject}
          aria-describedby={errors.subject ? 'subject-error' : undefined}
        />
        {errors.subject && (
          <p id="subject-error" className="mt-1 text-sm text-red-600" role="alert">
            {errors.subject.message}
          </p>
        )}
      </div>

      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-slate-700 mb-1">
          {t.contato.form.labels.message} <span className="text-red-500">*</span>
        </label>
        <textarea
          id="contact-message"
          rows={5}
          className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 outline-none transition resize-y min-h-[120px]"
          placeholder={t.contato.form.placeholders.message}
          {...register('message')}
          aria-invalid={!!errors.message}
          aria-describedby={errors.message ? 'message-error' : undefined}
        />
        {errors.message && (
          <p id="message-error" className="mt-1 text-sm text-red-600" role="alert">
            {errors.message.message}
          </p>
        )}
      </div>

      {submitStatus === 'success' && (
        <div
          className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-800"
          role="status"
        >
          {submitMessage}
        </div>
      )}
      {submitStatus === 'error' && (
        <div
          className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-800"
          role="alert"
        >
          {submitMessage}
        </div>
      )}

      <Button
        type="submit"
        isLoading={submitStatus === 'loading'}
        loadingText={t.common.sending}
        className="w-full sm:w-auto"
      >
        {t.common.sendMessage}
      </Button>
    </form>
  )
}
